import { useTranslation } from "react-i18next"
import Tooltip from "@mui/material/Tooltip"
import Stack from "@mui/material/Stack"
import Box from "@mui/material/Box"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"

import type { EntityType } from "types/common.types"
import type { AlertSettingParam } from "types/alerts.types"

import ControlledInput from "widgets/common/ControlledInput"
import { getAlertSettingParamRules } from "helpers/utils/alerts"
import ThresholdForm from "./ThresholdForm"
import DeviceTelemetryInput from "./device/DeviceTelemetryInput"
import PowerlineTelemetryInput from "./powerline/PowerlineTelemetryInput"
import TowerTelemetryInput from "./tower/TowerTelemetryInput"

const parseDescription = (text: string, paramId: string) => {
  const paragraphs = text.split("\n\n")
  return paragraphs.map((paragraph, pIndex) => (
    <Box key={`${paramId}-${pIndex}`} sx={{ mb: pIndex < paragraphs.length - 1 ? 1 : 0 }}>
      {paragraph}
    </Box>
  ))
}

interface AlertSettingInputProps {
  entity: EntityType
  entityId: number
  param: AlertSettingParam
}

const getParamInputType = (type: string) => {
  switch (type) {
    case "options": {
      return "select"
    }
    case "bool": {
      return "checkbox"
    }
    case "float":
    case "number": {
      return "number"
    }
    default: {
      return "text"
    }
  }
}

const AlertSettingInput = ({ param, entity, entityId }: AlertSettingInputProps) => {
  const inputType = getParamInputType(param.type)
  const isCheckbox = inputType === "checkbox"
  const selectOptions =
    inputType === "select" && param?.schema && "oneOf" in param.schema
      ? param.schema.oneOf.map((item) => ({
          label: item.label,
          value: item.const,
        }))
      : undefined
  const { t } = useTranslation()
  const rules = getAlertSettingParamRules(param, t)

  const getInput = (paramId: string) => {
    switch (paramId) {
      case "fields":
        switch (entity) {
          case "powerline":
            return <PowerlineTelemetryInput param={param} />
          case "tower":
            return <TowerTelemetryInput param={param} towerId={entityId} />
          case "device":
          default:
            return <DeviceTelemetryInput param={param} deviceId={entityId} />
        }
      case "thresholds":
        return <ThresholdForm param={param} rules={rules} />
      default:
        return (
          <ControlledInput
            type={inputType}
            placeholder=""
            name={param.id}
            label={param.name}
            defaultValue={param.default ?? ""}
            selectOptions={selectOptions ? selectOptions : null}
            variant="outlined"
            defaultChecked={!!param.default}
            rules={rules}
            endAdornment={param.unit}
          />
        )
    }
  }

  return (
    <Stack
      sx={{
        flexDirection: "row",
        width: isCheckbox ? "fit-content" : "100%",
        gap: isCheckbox ? "0rem" : "1rem",
        mb: "1rem",
        mr: isCheckbox ? "2rem" : 0,
        alignItems: "center",
        height: "fit-content",
      }}
    >
      {getInput(param.id)}
      {param.description && (
        <Tooltip
          title={<Box>{parseDescription(param.description, param.id)}</Box>}
          enterTouchDelay={0}
        >
          <InfoOutlinedIcon fontSize="medium" color="primary" />
        </Tooltip>
      )}
    </Stack>
  )
}

export default AlertSettingInput
